// Looking back over a game without touching it (§29 U4).
//
// **The command log is the save format, so an earlier position is a prefix of
// it replayed** — `watchCpu.ts`'s one architectural fact, and the same caveat
// applies here: scrubbing must *not* truncate, because the game is still being
// played underneath. Truncation is `GameStore.undo`, a different, deliberate
// act.
//
// **A step is a ply advance and nothing else.** Construction, machine policy
// and every other free action fold into the step that follows them: the board
// worth looking at is the one a move left behind. A list of "commands that
// count" would be an exclusion somebody has to remember to extend, whose
// failure is silent — `rules/turns.ts` refuses that shape for turn boundaries
// and this refuses it for the same reason.
//
// Pure and holds no React. Beside the store rather than in `src/engine/rules/`,
// because it is a *view* of a log rather than a rule about one.

import { applyCommand, type Command, type GameState } from "src/engine/rules/commands";

/** One board the game passed through, and where the log was when it did. */
export interface HistoryStep {
  /**
   * The last log index applied to reach this board.
   *
   * `TurnStart.at`'s meaning, deliberately: **-1 is a real answer**, the base
   * the store measures from, which is where a continued run starts.
   */
  at: number;
  state: GameState;
}

/**
 * The steps of one log, and the board each one landed on.
 *
 * **The states are the memo.** Every step is computed once, when the command
 * that made it is recorded, and held — so stepping back and forth over a long
 * game replays nothing. There is no invalidation inside this object because
 * there is no way to invalidate one: a log that was truncated or replaced is a
 * *different* history, and the store throws this away and builds another
 * (`GameHistory.of`). That is the whole of "dropped when the log is truncated".
 */
export class GameHistory {
  private steps: HistoryStep[];
  /** The ply the last recorded command left behind. */
  private ply: number;
  /** How many log commands have been recorded. */
  private length = 0;

  constructor(base: GameState) {
    this.steps = [{ at: -1, state: base }];
    this.ply = base.position.ply;
  }

  /** Replay a whole log — what a rebuild after an undo, a load or a rewind costs. */
  static of(base: GameState, log: readonly Command[]): GameHistory {
    const history = new GameHistory(base);
    let state = base;
    for (const command of log) {
      state = applyCommand(state, command);
      history.push(command, state);
    }
    return history;
  }

  /**
   * Record the board a command landed on.
   *
   * Handed the state rather than computing it, because the store has already
   * paid for that replay — so keeping the steps costs an integer comparison
   * per dispatch and not a second board.
   *
   * **`new-game` is a floor and resets the whole list**, which is what makes
   * First "the state after the last `new-game`": nothing before one is a
   * position of *this* game, and the menu dispatches one into a live store.
   */
  push(command: Command, state: GameState): void {
    const at = this.length++;
    if (command.type === "new-game") {
      this.steps = [{ at, state }];
      this.ply = state.position.ply;
      return;
    }
    if (state.position.ply > this.ply) this.steps.push({ at, state });
    this.ply = state.position.ply;
  }

  /** How many boards there are to look at — First, then one per step. */
  get count(): number {
    return this.steps.length;
  }

  /** The log indices the steps landed on, First's floor included. */
  get indices(): readonly number[] {
    return this.steps.map((step) => step.at);
  }

  /** One step, clamped: a view that outlives its log lands on the nearest board. */
  step(index: number): HistoryStep {
    return this.steps[Math.min(Math.max(index, 0), this.steps.length - 1)];
  }
}
